import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ShieldCheck, FlaskConical, FileCheck2, Thermometer, ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCoaLibrary } from "@/lib/products";
import Reveal from "@/components/Reveal";
import VialImage from "@/components/VialImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUIREMENTS = [
  ["Independent COA per lot", "Every lot must carry a Certificate of Analysis from a third-party analytical laboratory — HPLC purity and LC-MS identity at minimum."],
  ["Unaltered reproduction", "Certificates are published on Arias Peptides exactly as issued. We never edit, summarize away, or re-brand a supplier's analytical record."],
  ["Research-use-only labeling", "Vials, inserts and listings must be labeled for in-vitro laboratory research. No dosing, no wellness or therapeutic language."],
  ["Lot traceability", "Lot numbers on the vial, on the COA and in our catalog must match, so a buyer can audit the chain from bench to batch."],
  ["Cold-chain handling", "Lyophilized materials stored at -20°C, liquids at 2–8°C, with insulated, temperature-monitored dispatch."],
  ["Purchaser Agreement compliance", "Suppliers honor the same Research Purchaser Agreement every buyer signs — orders flagged by compliance review are declined."],
];

function SupplierApplyForm() {
  const [form, setForm] = useState({ name: "", entity: "", email: "", volume: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, volume: form.volume || "Supplier application",
        message: `[SUPPLIER APPLICATION] ${form.message}` });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit application");
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 p-6 text-center" data-testid="supplier-apply-success">
        <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
        <p className="font-display font-semibold text-slate-900 mt-3">Application received</p>
        <p className="text-sm text-slate-600 mt-1">We'll review your testing documentation and reply within 3 business days.</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="supplier-apply-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label htmlFor="sa-name">Contact name *</Label><Input id="sa-name" required value={form.name} onChange={set("name")} data-testid="supplier-apply-name" className="rounded-sm mt-1.5" /></div>
        <div><Label htmlFor="sa-entity">Brand / manufacturer *</Label><Input id="sa-entity" required value={form.entity} onChange={set("entity")} data-testid="supplier-apply-entity" className="rounded-sm mt-1.5" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label htmlFor="sa-email">Email *</Label><Input id="sa-email" type="email" required value={form.email} onChange={set("email")} data-testid="supplier-apply-email" className="rounded-sm mt-1.5" /></div>
        <div><Label htmlFor="sa-volume">Catalog size / monthly lots</Label><Input id="sa-volume" placeholder="e.g. 40 SKUs · 12 lots / month" value={form.volume} onChange={set("volume")} data-testid="supplier-apply-volume" className="rounded-sm mt-1.5" /></div>
      </div>
      <div><Label htmlFor="sa-msg">Testing laboratory & COA process *</Label>
        <Textarea id="sa-msg" required rows={4} placeholder="Which independent lab issues your COAs? Which methods (HPLC-UV, LC-MS, endotoxin)? Link to a sample certificate." value={form.message} onChange={set("message")} data-testid="supplier-apply-message" className="rounded-sm mt-1.5" /></div>
      <Button type="submit" disabled={busy} data-testid="supplier-apply-submit" className="gap-2">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit application
      </Button>
    </form>
  );
}

export default function Suppliers() {
  const coa = useCoaLibrary();
  const verified = coa.filter((r) => r.has_file);
  const pure = verified.filter((r) => r.purity);
  const avg = pure.length ? (pure.reduce((a, r) => a + r.purity, 0) / pure.length).toFixed(2) : "—";
  const sample = coa.find((r) => r.product_id === "abl-bpc-10");

  return (
    <main data-testid="suppliers-page">
      <section className="bg-[#0B111A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-[1.3fr_1fr] gap-12 items-end">
          <div>
            <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-400 mb-3">Supplier directory</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.04]">
              One marketplace.<br /><span className="italic font-medium text-slate-400">Verified manufacturers only.</span>
            </h1>
            <p className="mt-6 text-base text-slate-300 leading-relaxed max-w-xl">
              Arias Peptides does not manufacture. We list research compounds from manufacturers who meet a strict,
              published testing standard — independent lot-level Certificates of Analysis, reproduced unaltered.
              Axis Bio Lab is our first verified manufacturer; the door is open to brands who test the same way.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-6">
            {[["1", "Verified manufacturer"], [verified.length || "—", "Lots with COA"], [avg === "—" ? avg : `${avg}%`, "Mean HPLC purity"]].map(([v, l]) => (
              <div key={l}><p className="font-tech text-2xl sm:text-3xl font-semibold text-emerald-400">{v}</p><p className="text-xs text-slate-400 mt-1 uppercase tracking-wide">{l}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="supplier-axis-card">
        <Reveal className="grid lg:grid-cols-[220px_1fr] gap-10 border border-slate-200 bg-white p-6 sm:p-10">
          <div className="flex items-center justify-center bg-slate-50 border border-slate-100 p-4">
            {sample && <VialImage product={{ id: sample.product_id, name: sample.name, alias: sample.alias, specification: sample.specification, coa_purity: sample.purity, coa_lot: sample.lot }} className="h-64" />}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 font-tech text-[10px] uppercase tracking-[0.16em] border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-1"><ShieldCheck className="h-3.5 w-3.5" /> Verified manufacturer · 01</span>
              <span className="font-tech text-[10px] uppercase tracking-[0.16em] text-slate-400">Listed since 2026</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-4">Axis Bio Lab</h2>
            <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-2xl">
              Research-peptide manufacturer with lot-level Certificates of Analysis issued by independent laboratories
              (Freedom Diagnostics and partner labs). Every Axis vial on Arias Peptides carries the Axis Bio label, its lot
              number, and a downloadable certificate covering chromatographic purity, LC-MS identity and unit mass.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              {[[FlaskConical, "HPLC-UV 214 nm", "XBridge BEH C18 purity assay on every lot"], [FileCheck2, `${verified.length} COAs published`, "Original PDFs in the COA Library"], [Thermometer, "-20°C lyophilized", "Cold-chain from manufacturer to bench"]].map(([Icon, t, d]) => (
                <div key={t} className="border border-slate-200 p-4">
                  <Icon className="h-4 w-4 text-sky-700" />
                  <p className="font-tech text-sm font-semibold text-slate-900 mt-3">{t}</p>
                  <p className="text-xs text-slate-500 mt-1">{d}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/catalog" data-testid="supplier-axis-catalog-link" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-sky-700 text-white font-medium text-sm px-5 py-2.5 rounded-full transition-colors">Browse Axis Bio SKUs <ArrowUpRight className="h-4 w-4" /></Link>
              <Link to="/coa-library" data-testid="supplier-axis-coa-link" className="inline-flex items-center gap-2 border border-slate-300 hover:border-sky-700 hover:text-sky-700 text-slate-700 font-medium text-sm px-5 py-2.5 rounded-full transition-colors">COA Library</Link>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal className="mb-10">
            <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-3">Listing standard</p>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">What every manufacturer must meet.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-slate-200" data-testid="supplier-requirements">
            {REQUIREMENTS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05} className="border-r border-b border-slate-200 p-6">
                <p className="font-tech text-xs text-slate-400">0{i + 1}</p>
                <h3 className="font-display text-lg font-semibold text-slate-900 mt-3">{t}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-[1fr_1.2fr] gap-12">
        <Reveal>
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-3">Become a verified manufacturer</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Sell through Arias Peptides.</h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            If your lots are independently tested and you label for research use only, we'd like to list you.
            Tell us about your laboratory partner and share a sample certificate — we review documentation,
            request a reference lot, and onboard brands that pass.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-slate-600">
            {["No listing fee during onboarding", "Your brand stays on the vial and the COA", "Orders, agreements and payments handled by Arias"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1} className="border border-slate-200 bg-white p-6 sm:p-8">
          <SupplierApplyForm />
        </Reveal>
      </section>
    </main>
  );
}
